import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Monitor, Car, Heart, Users, Shield, Sprout } from 'lucide-react';
import CTA from '../../components/CTA';

const CookstownHypnotherapy: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Hypnotherapy Cookstown | Serving Mid Ulster from Belfast Clinic</title>
        <meta
          name="description"
          content="Professional hypnotherapy for Cookstown and Mid Ulster. Online sessions recommended. In-person available at Belfast clinic on the Ormeau Road. Anxiety, stress, chronic pain, agricultural community support. CNHC registered."
        />
        <meta name="keywords" content="hypnotherapy Cookstown, hypnotherapist Cookstown, anxiety treatment Cookstown, stress management Cookstown, chronic pain Cookstown, therapist Cookstown, market town, Mid Ulster" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/cookstown-hypnotherapy/" />
        <meta property="og:title" content="Hypnotherapy Cookstown | Serving Mid Ulster from Belfast Clinic" />
        <meta property="og:description" content="Professional hypnotherapy for Cookstown and Mid Ulster. In-person and online sessions. Supporting market town community with anxiety, stress, chronic pain treatment." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Serving Cookstown",
            "description": "CNHC registered hypnotherapy serving Cookstown and Mid Ulster from our Belfast clinic. Specializing in anxiety, stress, chronic pain management, and supporting agricultural communities. Both in-person and online sessions available.",
            "url": "https://www.sharonclare-hypnotherapy.com/cookstown-hypnotherapy/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "areaServed": {
              "@type": "City",
              "name": "Cookstown, County Tyrone",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "location": [
              {
                "@type": "Place",
                "name": "The Arzen Collective",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "The Arzen Collective, 13B Antrim Street",
                  "addressLocality": "Lisburn",
                  "addressRegion": "County Antrim",
                  "postalCode": "BT28",
                  "addressCountry": "GB"
                }
              },
              {
                "@type": "Place",
                "name": "Belfast Clinic",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                  "addressLocality": "Belfast",
                  "addressRegion": "County Down",
                  "postalCode": "BT7 2FZ",
                  "addressCountry": "GB"
                }
              }
            ],
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ],
            "medicalSpecialty": "Clinical Hypnotherapy",
            "serviceType": [
              "Anxiety Treatment",
              "Stress Management",
              "Chronic Pain Management",
              "Smoking Cessation",
              "Weight Management"
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Professional Hypnotherapy Serving Cookstown
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Online sessions, or the Belfast and Lisburn clinics
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3 mb-3">
                <Monitor className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">Online sessions any day</span> — no travel from Cookstown
                </p>
              </div>
              <div className="flex items-center justify-center space-x-3">
                <MapPin className="w-6 h-6 text-primary-600" />
                <p className="text-gray-600">Or in person at the Belfast clinic on the Ormeau Road, or the Lisburn clinic on Antrim Street</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Hypnotherapy for Cookstown and Mid Ulster
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                I'm proud to serve the Mid Ulster community. As a CNHC registered hypnotherapist with 15 years of nursing experience at Belfast City Hospital, I offer both in-person and online options. For Cookstown residents, <strong>online sessions</strong> are usually the most practical choice - available throughout the week with no travel at all, providing the same professional, evidence-based treatment from home. If you would rather meet face to face, I hold clinics at Belfast Wellness Clinic, 228 Ormeau Road, Belfast, and at The Arzen Collective, 13B Antrim Street, Lisburn; days and times vary, and early evening appointments are available.
              </p>
              <p>
                Online hypnotherapy sessions via secure video call provide exactly the same CNHC-registered treatment as in-person appointments, with the flexibility to fit therapy around your life rather than the other way around. For those who do prefer face-to-face treatment, current Belfast clinic availability and online booking are on the{' '}
                <a
                  href="https://belfastchiropracticclinic.janeapp.co.uk/#/staff_member/37"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 font-medium underline"
                >
                  clinic booking page
                </a>
                , and early evening appointments are available. Many Cookstown clients choose an initial in-person consultation followed by online sessions for regular ongoing support.
              </p>
              <p>
                Cookstown is a distinctive market town in the heart of Mid Ulster, with a population of around 12,600. Known for having one of the longest and widest main streets in Ireland - a remarkable 1.25 miles long and 135 feet wide - Cookstown retains a strong identity as a traditional market town with deep agricultural connections. Founded in 1620 during the Plantation of Ulster, the town has served for over 400 years as a commercial and social hub for the surrounding rural farming community. This heritage creates a tight-knit, community-oriented character that has both strengths and challenges for mental health.
              </p>
              <p>
                While smaller than neighboring Dungannon, Cookstown proudly brands itself as the "Retail Capital of Mid Ulster" and has experienced significant economic growth over the past decade - becoming the third fastest-growing district in Northern Ireland for employment. However, living in a smaller market town where everyone seems to know everyone, combined with the economic pressures facing both retail businesses and the agricultural sector, can create unique stresses. Social dynamics in close communities, the challenge of maintaining privacy around mental health concerns, work-life balance for small business owners working long hours, and the isolation that can affect farming families despite being surrounded by neighbors - these are all real issues that impact wellbeing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Supporting Cookstown's Market Town Community Section */}
      <section className="py-16 sm:py-20 bg-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              Supporting Cookstown's Market Town and Agricultural Community
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Cookstown's identity is deeply rooted in its agricultural heritage. The town has served as a market center for livestock and goods since 1628, when King Charles I granted Letters Patent for twice-weekly markets. While the nature of agriculture has changed dramatically over nearly 400 years, the connection between Cookstown and the surrounding farming community remains strong. Many residents are involved in agriculture directly - running family farms, working in agricultural services, or operating businesses that support the farming sector - or have family ties to the land.
              </p>
              <p>
                The farming community faces particular mental health challenges that are often overlooked. Financial pressures from volatile commodity prices, weather-dependent incomes, and the economic squeeze on small family farms create chronic anxiety and stress. Physical demands and chronic pain from years of manual labor are common. Rural isolation - working long hours alone on the land with limited social contact - can lead to loneliness and depression. For farms passed down through generations, there's often immense pressure around succession, the fear of being the one who loses the family land, and identity deeply tied to farming that makes it hard to step away even when struggling.
              </p>
              <p>
                I understand that accessing mental health support in a small town comes with concerns about privacy and confidentiality. In Cookstown, where community bonds are strong but anonymity is limited, it can feel difficult to seek help without worrying about who might see your car outside a therapist's office or who might hear about it through social connections. This is where online sessions offer real advantages - you can attend therapy from the comfort of your own home without any concern about being seen. For those who prefer in-person sessions, the Belfast clinic on the Ormeau Road is far enough from Cookstown that you are unlikely to bump into neighbors, providing additional discretion.
              </p>
              <p>
                For Cookstown's retail business community, the pressures are different but equally real. Small business owners - running shops on the famous Main Street, managing cafes and restaurants, operating service businesses - often work extraordinarily long hours with minimal time off. The financial responsibility of employing staff, meeting rent, and competing with online retail creates constant pressure. Work-life balance becomes nearly impossible when you're the owner and the business depends entirely on you. Many business owners struggle with anxiety about finances, exhaustion from overwork, and the difficulty of switching off when home and work life blur together in a small town where you live and work in the same community.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How I Can Support Cookstown Residents
            </h2>
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-primary-50 rounded-xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Shield className="w-8 h-8 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Anxiety and Stress Management
                    </h3>
                    <p className="text-gray-700">
                      Whether you're experiencing anxiety related to financial pressures, business worries, farming sector uncertainty, or general stress, hypnotherapy can help you develop calm and control. For business owners lying awake at night worrying about cashflow, or farmers anxious about weather and markets, solution-focused hypnotherapy builds practical resilience and coping strategies.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-accent-50 rounded-xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Sprout className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Rural Isolation and Loneliness
                    </h3>
                    <p className="text-gray-700">
                      For farming families and rural residents around Cookstown, isolation can be a real issue despite being surrounded by neighbors. Long hours working alone, limited social contact, and the challenge of maintaining connections when you're tied to the land can all contribute to loneliness and low mood. Hypnotherapy helps build resilience and develop strategies for maintaining wellbeing despite isolation.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-accent-50 rounded-xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Heart className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Chronic Pain Management
                    </h3>
                    <p className="text-gray-700">
                      For farmers, agricultural workers, and those in physically demanding roles, chronic pain is a common challenge. Back pain, arthritis, repetitive strain injuries, and work-related musculoskeletal conditions can all benefit from hypnotherapy. Research shows hypnotherapy can reduce pain intensity by 29% on average, offering relief alongside medical treatment and enabling you to continue working despite pain.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-primary-50 rounded-xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Clock className="w-8 h-8 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Work-Life Balance and Burnout
                    </h3>
                    <p className="text-gray-700">
                      Small business owners and farmers often struggle with work-life balance. When you own the business or the farm, you never truly switch off. Hypnotherapy can help establish healthier boundaries, reduce anxiety about work when you're meant to be resting, and develop strategies for sustainable working patterns that don't lead to exhaustion and burnout.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <h3 className="text-2xl font-heading font-semibold text-gray-900 mt-8 mb-4">
                Smoking Cessation for Cookstown Residents
              </h3>
              <p>
                Many people in Cookstown - particularly farmers and outdoor workers - smoke as a stress management strategy. However, smoking is expensive (thousands of pounds a year for a 20-a-day habit) and creates long-term health risks that can threaten your ability to continue working physically demanding jobs as you age. Hypnotherapy for smoking cessation works by breaking the psychological addiction and stress-relief association, making it easier to quit without intense cravings. Many motivated clients quit successfully after 3-4 sessions. For farming families and business owners concerned about finances, the cost savings from quitting smoking can be genuinely significant.
              </p>

              <h3 className="text-2xl font-heading font-semibold text-gray-900 mt-8 mb-4">
                Weight Management and Emotional Eating
              </h3>
              <p>
                Weight management isn't about willpower - it's about addressing the emotional patterns that drive eating behaviors. Many people in Cookstown struggle with emotional eating, comfort eating during stressful times, and the frustration of yo-yo dieting. For business owners and farmers dealing with work stress, food can become a coping mechanism. Solution-focused hypnotherapy helps you develop a healthier relationship with food, recognize emotional eating triggers, and create lasting behavioral shifts without restrictive dieting that feels like punishment.
              </p>

              <h3 className="text-2xl font-heading font-semibold text-gray-900 mt-8 mb-4">
                Evidence-Based Treatment You Can Trust
              </h3>
              <p>
                I'm registered with the Complementary and Natural Healthcare Council (CNHC), the UK's government-backed regulator for complementary therapies. This registration requires 450+ hours of professional training, ongoing CPD, professional indemnity insurance, and adherence to strict ethical guidelines. My 15 years as a registered nurse at Belfast City Hospital provides additional medical context and understanding of complex health conditions, chronic pain, and the health impacts of physically demanding work.
              </p>
              <p>
                Hypnotherapy isn't magic - it's an evidence-based therapeutic technique that works with your brain's natural neuroplasticity. Research shows it's effective for anxiety (60% reduction in symptoms), chronic pain management (29% pain reduction), sleep problems, and habit change when the client is motivated. I combine hypnotherapy with cognitive behavioral techniques, solution-focused therapy, and practical stress management strategies for comprehensive support.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Crisis Resources Section */}
      <section className="py-16 sm:py-20 bg-red-50 border-l-4 border-red-600">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">
              Crisis Support for Cookstown Residents
            </h2>
            <p className="text-lg text-gray-700 mb-6">
              If you're experiencing a mental health crisis, self-harm thoughts, or suicidal ideation, please reach out for immediate support. Hypnotherapy is not appropriate for crisis situations - you need urgent professional help.
            </p>
            <div className="bg-white rounded-xl p-6 space-y-4">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Northern Health and Social Care Trust Crisis Response</h3>
                <p className="text-gray-700 mb-1">24/7 mental health crisis support for Mid Ulster</p>
                <a href="tel:08001837745" className="text-primary-600 font-semibold text-lg hover:text-primary-700">
                  0800 183 7745
                </a>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Rural Support (Farming Community)</h3>
                <p className="text-gray-700 mb-1">24/7 helpline specifically for farming families and rural communities</p>
                <a href="tel:08085875787" className="text-primary-600 font-semibold text-lg hover:text-primary-700">
                  0808 587 5787
                </a>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Lifeline NI</h3>
                <p className="text-gray-700 mb-1">24/7 crisis helpline for anyone in distress or despair</p>
                <a href="tel:08088088000" className="text-primary-600 font-semibold text-lg hover:text-primary-700">
                  0808 808 8000
                </a>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Samaritans</h3>
                <p className="text-gray-700 mb-1">24/7 emotional support for anyone struggling to cope</p>
                <a href="tel:116123" className="text-primary-600 font-semibold text-lg hover:text-primary-700">
                  116 123
                </a>
              </div>
              <div className="pt-4 border-t border-gray-200">
                <p className="text-gray-700">
                  <strong>In an emergency:</strong> Call 999 or go to the emergency department at Causeway Hospital, Coleraine (nearest A&E to Cookstown).
                </p>
                <p className="text-gray-700 mt-2">
                  <strong>GP referral:</strong> For complex mental health needs including diagnosed conditions (depression, PTSD, bipolar disorder, schizophrenia, psychosis), please speak to your GP. Hypnotherapy can support mental wellness but is not a replacement for psychiatric care.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting to the Belfast Clinic from Cookstown Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              Getting to the Belfast Clinic from Cookstown
            </h2>

            <div className="bg-white rounded-xl p-6 mb-8">
              <div className="flex items-start space-x-4">
                <Car className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                    By Car - Practical for Regular Appointments
                  </h3>
                  <p className="text-gray-700 mb-4">
                    The journey from Cookstown to our Belfast clinic:
                  </p>
                  <ul className="space-y-2 text-gray-700 mb-4">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Route:</strong> Take the A29 south from Cookstown to the M1, then the M1 east into Belfast, and continue south along the Ormeau Road (A24)</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Distance:</strong> Approximately 45 miles</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Time:</strong> Approximately 50-55 minutes in normal traffic</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Destination:</strong> Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>On arrival:</strong> The clinic sits on the Ormeau Road in south Belfast, roughly 10 minutes from the city centre</span>
                    </li>
                  </ul>
                  <div className="bg-accent-50 rounded-lg p-4">
                    <p className="text-gray-700">
                      <strong>Online sessions recommended:</strong> At approximately 45 miles, the journey from Cookstown to south Belfast is manageable for occasional in-person sessions but can be a significant commitment for regular weekly appointments. Many Cookstown clients prefer the convenience of online sessions, which provide the same professional care without the travel time. Consider an initial in-person consultation followed by online sessions for ongoing support.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6">
              <div className="flex items-start space-x-4">
                <Monitor className="w-8 h-8 text-primary-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                    Online Sessions - Maximum Privacy and Convenience
                  </h3>
                  <p className="text-gray-700 mb-4">
                    While the drive into Belfast is manageable, online sessions offer additional benefits that many Cookstown clients value:
                  </p>
                  <ul className="space-y-2 text-gray-700 mb-4">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-primary-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Privacy:</strong> Attend from home without any concern about being seen - particularly valuable in a tight-knit community</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-primary-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Convenience:</strong> No travel time, fuel costs, or need to arrange time away from business or farm</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-primary-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Flexibility:</strong> Easier to fit sessions around farming schedules, retail hours, or family commitments</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-primary-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Weather-proof:</strong> No concerns about driving in poor winter weather or during busy farming seasons</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-primary-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Equally effective:</strong> Research shows online hypnotherapy produces the same therapeutic outcomes as in-person sessions</span>
                    </li>
                  </ul>
                  <p className="text-gray-700">
                    Online sessions use secure, GDPR-compliant video calling (Zoom or similar). You'll need a private space, stable internet connection, and a device with a camera (laptop, tablet, or smartphone). Sessions are conducted exactly as in-person appointments, with the same professionalism, privacy, and therapeutic effectiveness. Many clients combine in-person and online sessions based on their schedule and circumstances - whatever works best for you.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Sessions and Packages
            </h2>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-gray-50 rounded-xl p-6 text-center">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">Initial Consultation</h3>
                <p className="text-gray-600">90 minutes including assessment and first session</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-6 text-center">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">Single Session</h3>
                <p className="text-gray-600">60-minute follow-up session</p>
              </div>
              <div className="bg-accent-100 rounded-xl p-6 text-center border-2 border-accent-600">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">4-Session Package</h3>
                <p className="text-gray-600">Best value for ongoing work, at a discount on single sessions</p>
              </div>
            </div>
            <p className="text-center text-gray-600 mb-4">
              Choose whichever format works best for you.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Common Questions from Cookstown Clients
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Is the journey from Cookstown to the Belfast clinic manageable for regular appointments?
                </h3>
                <p className="text-gray-700">
                  The Belfast clinic is approximately 45 miles from Cookstown (around an hour via the M1 and then the Ormeau Road), which is manageable for occasional in-person sessions but can be a significant commitment for weekly appointments. Many Cookstown clients prefer online sessions for regular appointments, as they provide the same professional treatment without any travel. Some clients choose an initial in-person consultation at the Belfast clinic followed by ongoing online sessions - this hybrid approach works well for many people.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Will people in Cookstown know I'm attending therapy?
                </h3>
                <p className="text-gray-700">
                  Privacy is a valid concern in close-knit communities where anonymity can be limited. Online sessions allow you to attend therapy from the comfort of your own home without any concern about being seen - this is the most private option available. For those who attend in person, the Belfast clinic on the Ormeau Road is far enough from Cookstown that you are very unlikely to encounter neighbors or acquaintances. I take confidentiality extremely seriously - what we discuss remains strictly between us, regardless of whether you attend in person or online.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with farming-related stress and anxiety?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. The farming community faces unique pressures - financial uncertainty from volatile markets and weather, physical demands and chronic pain, rural isolation, long hours working alone, and for family farms, the weight of generational expectations. Solution-focused hypnotherapy is highly effective for this type of chronic stress and anxiety. We focus on building resilience, developing coping strategies for managing uncertainty, and strengthening your mental wellbeing even when external circumstances (weather, prices, policies) feel beyond your control. Many farming clients find significant relief from anxiety and improved sleep within 4-6 sessions.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  I run a business on Main Street - can you help with work stress and burnout?
                </h3>
                <p className="text-gray-700">
                  Yes. Small business owners face particular challenges - long hours, financial responsibility for staff and rent, difficulty switching off, and work-life balance that becomes nearly impossible when you're the owner. Hypnotherapy can help you manage work-related anxiety, establish healthier boundaries between work and personal life, reduce the constant mental chatter about business concerns, and develop sustainable working patterns that don't lead to exhaustion and burnout. Many business owners report sleeping better, feeling less overwhelmed, and maintaining better mental health despite demanding schedules.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  How does hypnotherapy help with chronic pain from physical work?
                </h3>
                <p className="text-gray-700">
                  Chronic pain from farming, manual labor, or physically demanding work responds well to hypnotherapy. Pain is processed in the brain, and research shows that stress and anxiety intensify pain signals significantly. Solution-focused hypnotherapy works by calming your nervous system, reducing pain perception, and teaching your brain healthier patterns of pain processing. Most clients experience reductions in pain intensity (research shows 29% average reduction on average) and improvements in their ability to function and continue working despite pain. Hypnotherapy works alongside medical treatment, physiotherapy, and pain medication - not as a replacement.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Are online sessions really as effective as in-person?
                </h3>
                <p className="text-gray-700">
                  Yes. Research demonstrates that online hypnotherapy produces the same therapeutic outcomes as face-to-face sessions. The hypnotic trance state is just as deep, the therapeutic techniques are identical, and the relationship between therapist and client develops equally well. Many clients actually find it easier to relax at home in their own familiar environment. Online sessions require a private space, stable internet connection, and a device with a camera (laptop, tablet, or smartphone), but otherwise the experience and effectiveness are equivalent to in-person treatment.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Can I mix in-person and online sessions?
                </h3>
                <p className="text-gray-700">
                  Absolutely. Many Cookstown clients do exactly this - perhaps attending the initial consultation in person at the Belfast clinic to meet me face-to-face, then switching to online sessions for convenience, or doing most sessions online but occasionally coming to the clinic in person. You can choose what works best for your schedule, circumstances, and preferences on a session-by-session basis. The therapeutic approach and effectiveness remain consistent whether in-person or online.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  How many sessions will I need?
                </h3>
                <p className="text-gray-700">
                  This varies depending on your goals and circumstances. Some clients achieve their goals in 3-4 sessions (smoking cessation, specific phobias), while others benefit from 6-8 sessions for more complex issues (chronic anxiety, pain management, long-term stress). We'll discuss realistic expectations in your initial consultation and review progress regularly. There's no pressure to commit to multiple sessions upfront - you can decide after each appointment whether to continue.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Is hypnotherapy safe? Will I lose control?
                </h3>
                <p className="text-gray-700">
                  Hypnotherapy is completely safe and you remain in control throughout. Hypnosis is a natural state of focused attention - similar to being absorbed in a book or film - not unconsciousness or sleep. You can't be made to do anything against your will, you'll remember everything, and you can open your eyes and end the session at any time. It's a collaborative process where you're an active participant in your own healing. Many people are surprised by how relaxed yet aware they feel during hypnosis.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Do you accept health insurance?
                </h3>
                <p className="text-gray-700">
                  Hypnotherapy is a private service and not covered by the NHS. However, some private health insurance policies (Benenden Health, Westfield Health, Vitality Health) include complementary therapy benefits. Check your policy details. Payment is required at the time of booking. I provide receipts that you can submit to your insurer for potential reimbursement. Many clients find the investment worthwhile given the significant improvement in their quality of life.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              What Mid Ulster Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-accent-50 rounded-xl p-6">
                <p className="text-gray-700 italic mb-4">
                  "I'm a farmer and the financial stress was overwhelming - weather, prices, everything felt out of control. I was lying awake at night worrying and it was affecting my health. Sharon's hypnotherapy helped me manage the anxiety and sleep properly again. I can't control the weather or the markets, but I can control how I respond. The online sessions were perfect - I could fit them around farm work without traveling."
                </p>
                <p className="text-gray-900 font-semibold">- D.H., Mid Ulster</p>
              </div>

              <div className="bg-primary-50 rounded-xl p-6">
                <p className="text-gray-700 italic mb-4">
                  "Running a business on Main Street meant I was working 70-hour weeks and couldn't switch off. The anxiety about finances and staff was constant. Sharon helped me establish better boundaries and manage the stress. I'm sleeping better, I've stopped the panic attacks, and I actually take time off now. The drive to Belfast became my time to focus on myself instead of work."
                </p>
                <p className="text-gray-900 font-semibold">- M.C., Cookstown</p>
              </div>

              <div className="bg-accent-50 rounded-xl p-6">
                <p className="text-gray-700 italic mb-4">
                  "Chronic back pain from years of farm work was limiting what I could do. I was worried I'd have to give up farming altogether. Sharon's hypnotherapy approach helped me manage the pain much better - it's still there but it doesn't control my life anymore. I've reduced my pain medication and I can work more comfortably. Being able to do sessions online when the pain was bad was really helpful."
                </p>
                <p className="text-gray-900 font-semibold">- J.K., County Tyrone</p>
              </div>

              <div className="bg-primary-50 rounded-xl p-6">
                <p className="text-gray-700 italic mb-4">
                  "I chose online sessions because I valued the privacy - in a small town like Cookstown, everyone knows everyone. The online format meant I could get help without worrying about being seen. Sharon's approach was completely confidential and non-judgmental. My anxiety has improved dramatically and I feel like I can cope with daily life again."
                </p>
                <p className="text-gray-900 font-semibold">- L.T., Mid Ulster</p>
              </div>

              <div className="bg-accent-50 rounded-xl p-6">
                <p className="text-gray-700 italic mb-4">
                  "I quit smoking after 22 years with Sharon's help. Three sessions and I haven't had a cigarette in 8 months. The money I'm saving is incredible - hundreds of pounds a month that now goes toward the family instead of tobacco. I feel healthier, I can breathe properly working outdoors, and I'm not controlled by the addiction anymore. Best decision I made."
                </p>
                <p className="text-gray-900 font-semibold">- P.M., County Tyrone</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <CTA />
    </main>
  );
};

export default CookstownHypnotherapy;
